import { useTranslate } from "@/lib/i18n";
import { useApplyDashboardTheme } from "@/app/themes/dashboardTheme";
import { SystemIllustration } from "./SystemIllustration";

/**
 * SPLASH — the app's boot screen (design: `system-view="splash"`).
 *
 * Shown by the two doors that wait on the session and store binding —
 * `LandingRedirect` and `ProtectedLayout` — in place of the old skeleton; each
 * calls `useSplashGate(pending)` itself so it can KEEP rendering this for the
 * minimum hold after the session lands. Until the gate says `visible` it paints nothing but a labelled, busy region,
 * so a fast boot never flashes the illustration (see `useSplashGate`).
 *
 * The store is not known yet at boot (it IS the thing being resolved), so the
 * mark is the product's, not the restaurant's; the design's restaurant name
 * ("CANDELLA") is the design's sample data.
 */
export function SplashScreen({ visible }: { visible: boolean }) {
  const t = useTranslate();
  const opening = t((m) => m.systemScreens.splashOpening);
  // Boot happens before any shell has put the device's look on <body>; without
  // this the splash would paint in the platform's colours (black accent), not
  // PAPER's / STACK's. Removed again when the splash goes.
  useApplyDashboardTheme();

  if (!visible) {
    return (
      <div
        data-ui="splash-pending"
        role="status"
        aria-busy="true"
        aria-label={opening}
        className="min-h-dvh"
      />
    );
  }

  return (
    <div data-ui="splash" role="status" aria-busy="true" aria-live="polite">
      <div data-ui="splash-blob" aria-hidden="true" />
      <div data-ui="splash-art">
        <SystemIllustration art="splash" />
      </div>
      <div data-ui="splash-brand">
        <div data-ui="splash-mark">
          <span data-ui="splash-glyph" aria-hidden="true">
            T
          </span>
          <span data-ui="splash-name">TRIOTY</span>
        </div>
        <span data-ui="splash-status">{opening}</span>
      </div>
      <div data-ui="splash-track" aria-hidden="true">
        <div data-ui="splash-bar" />
      </div>
      <span data-ui="splash-powered">{t((m) => m.systemScreens.splashPowered)}</span>
    </div>
  );
}
